"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  IconButton,
  Paper,
  Grid,
  Divider,
} from "@mui/material"
import {
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Crop as CropIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"

const ImageEditorDialog = ({ open, onClose, imageFile, onConfirm, title = "Edit Image" }) => {
  const canvasRef = useRef(null)
  const cropCanvasRef = useRef(null)
  const containerRef = useRef(null)
  const [image, setImage] = useState(null)
  const [rotation, setRotation] = useState(0)
  const [zoom, setZoom] = useState(1)
  const [cropMode, setCropMode] = useState(false)
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [imageLoaded, setImageLoaded] = useState(false)
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 400 })

  // Load image when file changes
  // useEffect(() => {
  //   if (imageFile && open) {
  //     const img = new Image()
  //     img.crossOrigin = "anonymous"
  //     img.onload = () => {
  //       setImage(img)
  //       setImageLoaded(true)
  //       // Reset all transformations
  //       setRotation(0)
  //       setZoom(1)
  //       setCropMode(false)
  //       setCropArea({ x: 0, y: 0, width: 0, height: 0 })

  //       // Calculate optimal canvas size
  //       const maxWidth = 600
  //       const maxHeight = 400
  //       const aspectRatio = img.width / img.height

  //       let newWidth, newHeight
  //       if (aspectRatio > maxWidth / maxHeight) {
  //         newWidth = maxWidth
  //         newHeight = maxWidth / aspectRatio
  //       } else {
  //         newHeight = maxHeight
  //         newWidth = maxHeight * aspectRatio
  //       }

  //       setCanvasSize({ width: newWidth, height: newHeight })
  //     }
  //     img.src = URL.createObjectURL(imageFile)
  //   }
  // }, [imageFile, open])
  useEffect(() => {
    if (imageFile && open) {
      const reader = new FileReader();
  
      reader.onload = () => {
        const imageUrl = reader.result;
        const img = new Image();
        img.src = imageUrl;
  
        img.onload = () => {
          setImage(img);
          setImageLoaded(true);
          setRotation(0);
          setZoom(1);
          setCropMode(false);
          setCropArea({ x: 0, y: 0, width: 0, height: 0 });
  
          const maxWidth = 600;
          const maxHeight = 400;
          const aspectRatio = img.width / img.height;
  
          let newWidth, newHeight;
          if (aspectRatio > maxWidth / maxHeight) {
            newWidth = maxWidth;
            newHeight = maxWidth / aspectRatio;
          } else {
            newHeight = maxHeight;
            newWidth = maxHeight * aspectRatio;
          }
  
          setCanvasSize({ width: newWidth, height: newHeight });
        };
      };
  
      reader.readAsDataURL(imageFile); // Convert the image file to base64
    }
  }, [imageFile, open]);

  // Draw image on canvas
  const drawImage = useCallback(() => {
    if (!image || !canvasRef.current || !imageLoaded) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")

    canvas.width = canvasSize.width
    canvas.height = canvasSize.height

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Save context
    ctx.save()

    // Move to center
    ctx.translate(canvas.width / 2, canvas.height / 2)

    // Apply rotation
    ctx.rotate((rotation * Math.PI) / 180)

    // Apply zoom and draw image
    const scaledWidth = (image.width * zoom * canvas.width) / image.width
    const scaledHeight = (image.height * zoom * canvas.height) / image.height

    ctx.drawImage(image, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight)

    // Restore context
    ctx.restore()

    // Draw crop overlay if in crop mode
    if (cropMode && cropArea.width > 0 && cropArea.height > 0) {
      // Create a temporary canvas to draw the overlay properly
      const tempCanvas = document.createElement("canvas")
      const tempCtx = tempCanvas.getContext("2d")
      tempCanvas.width = canvas.width
      tempCanvas.height = canvas.height

      // Draw the original image on temp canvas
      tempCtx.save()
      tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2)
      tempCtx.rotate((rotation * Math.PI) / 180)
      const tempScaledWidth = (image.width * zoom * tempCanvas.width) / image.width
      const tempScaledHeight = (image.height * zoom * tempCanvas.height) / image.height
      tempCtx.drawImage(image, -tempScaledWidth / 2, -tempScaledHeight / 2, tempScaledWidth, tempScaledHeight)
      tempCtx.restore()

      // Apply dark overlay to entire canvas
      ctx.fillStyle = "rgba(0, 0, 0, 0.6)"
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Use composite operation to show original image in crop area
      ctx.globalCompositeOperation = "destination-out"
      ctx.fillStyle = "rgba(0, 0, 0, 1)"
      ctx.fillRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height)

      // Reset composite operation and draw original image in crop area
      ctx.globalCompositeOperation = "source-over"
      ctx.drawImage(
        tempCanvas,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
      )

      // Draw crop border
      ctx.strokeStyle = "#2196f3"
      ctx.lineWidth = 3
      ctx.setLineDash([8, 4])
      ctx.strokeRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height)
      ctx.setLineDash([])

      // Draw corner handles for better UX
      const handleSize = 8
      ctx.fillStyle = "#2196f3"
      // Top-left
      ctx.fillRect(cropArea.x - handleSize / 2, cropArea.y - handleSize / 2, handleSize, handleSize)
      // Top-right
      ctx.fillRect(cropArea.x + cropArea.width - handleSize / 2, cropArea.y - handleSize / 2, handleSize, handleSize)
      // Bottom-left
      ctx.fillRect(cropArea.x - handleSize / 2, cropArea.y + cropArea.height - handleSize / 2, handleSize, handleSize)
      // Bottom-right
      ctx.fillRect(
        cropArea.x + cropArea.width - handleSize / 2,
        cropArea.y + cropArea.height - handleSize / 2,
        handleSize,
        handleSize,
      )
    }
  }, [image, rotation, zoom, cropMode, cropArea, canvasSize, imageLoaded])

  // Redraw when dependencies change
  useEffect(() => {
    drawImage()
  }, [drawImage])

  // Handle rotation
  const handleRotate = (direction) => {
    setRotation((prev) => {
      const newRotation = direction === "left" ? prev - 90 : prev + 90
      return newRotation >= 360 ? newRotation - 360 : newRotation < 0 ? newRotation + 360 : newRotation
    })
  }

  // Handle zoom
  const handleZoomChange = (event, newValue) => {
    setZoom(newValue)
  }

  // Handle crop mode toggle
  const handleCropToggle = () => {
    setCropMode(!cropMode)
    if (!cropMode) {
      // Initialize crop area to center quarter of image
      const quarterWidth = canvasSize.width / 4
      const quarterHeight = canvasSize.height / 4
      setCropArea({
        x: canvasSize.width / 2 - quarterWidth / 2,
        y: canvasSize.height / 2 - quarterHeight / 2,
        width: quarterWidth,
        height: quarterHeight,
      })
    } else {
      setCropArea({ x: 0, y: 0, width: 0, height: 0 })
    }
  }

  // Handle mouse events for cropping
  const handleMouseDown = (e) => {
    if (!cropMode) return

    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    setIsDragging(true)
    setDragStart({ x, y })
    setCropArea({ x, y, width: 0, height: 0 })
  }

  const handleMouseMove = (e) => {
    if (!cropMode || !isDragging) return

    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const width = x - dragStart.x
    const height = y - dragStart.y

    setCropArea({
      x: width < 0 ? x : dragStart.x,
      y: height < 0 ? y : dragStart.y,
      width: Math.abs(width),
      height: Math.abs(height),
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Reset all transformations
  const handleReset = () => {
    setRotation(0)
    setZoom(1)
    setCropMode(false)
    setCropArea({ x: 0, y: 0, width: 0, height: 0 })
  }

  // Generate final edited image
  const generateEditedImage = () => {
    if (!image) return null

    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    // Determine final canvas size based on crop or original
    let finalWidth, finalHeight
    if (cropMode && cropArea.width > 0 && cropArea.height > 0) {
      finalWidth = cropArea.width
      finalHeight = cropArea.height
    } else {
      finalWidth = canvasSize.width
      finalHeight = canvasSize.height
    }

    canvas.width = finalWidth
    canvas.height = finalHeight

    if (cropMode && cropArea.width > 0 && cropArea.height > 0) {
      // For cropped image, we need to extract the cropped portion
      const sourceCanvas = canvasRef.current
      ctx.drawImage(
        sourceCanvas,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        finalWidth,
        finalHeight,
      )
    } else {
      // For non-cropped image, apply all transformations
      ctx.save()
      ctx.translate(canvas.width / 2, canvas.height / 2)
      ctx.rotate((rotation * Math.PI) / 180)

      const scaledWidth = (image.width * zoom * canvas.width) / image.width
      const scaledHeight = (image.height * zoom * canvas.height) / image.height

      ctx.drawImage(image, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight)
      ctx.restore()
    }

    return canvas
  }

  // Handle confirm
  const handleConfirm = () => {
    const editedCanvas = generateEditedImage()
    console.log("editedCanvas---------",editedCanvas);
    if (editedCanvas) {
      editedCanvas.toBlob(
        (blob) => {
          if (blob) {
            // Create a new File object with the edited image
            const editedFile = new File([blob], imageFile.name, {
              type: imageFile.type,
              lastModified: Date.now(),
            })
            onConfirm(editedFile)
          }
        },
        imageFile.type,
        0.9,
      )
    }
  }

  // Handle close
  const handleClose = () => {
    setImageLoaded(false)
    setImage(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Image Canvas */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, textAlign: "center", bgcolor: "grey.100" }}>
              <Box
                ref={containerRef}
                sx={{
                  position: "relative",
                  display: "inline-block",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <canvas
                  ref={canvasRef}
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    cursor: cropMode
                      ? 'url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2v6h6M18 6v6h6M18 22v-6h-6M6 18v-6H0"/></svg>\') 12 12, crosshair'
                      : "default",
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
              </Box>

              {cropMode && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Click and drag to select crop area
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Controls */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Image Controls
              </Typography>

              {/* Rotation Controls */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Rotation
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton onClick={() => handleRotate("left")} color="primary">
                    <RotateLeftIcon />
                  </IconButton>
                  <Typography variant="body2" sx={{ minWidth: 40, textAlign: "center" }}>
                    {rotation}°
                  </Typography>
                  <IconButton onClick={() => handleRotate("right")} color="primary">
                    <RotateRightIcon />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Zoom Controls */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Zoom: {Math.round(zoom * 100)}%
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ZoomOutIcon />
                  <Slider value={zoom} onChange={handleZoomChange} min={0.1} max={3} step={0.1} sx={{ flex: 1 }} />
                  <ZoomInIcon />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Crop Controls */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Crop
                </Typography>
                <Button
                  variant={cropMode ? "contained" : "outlined"}
                  startIcon={<CropIcon />}
                  onClick={handleCropToggle}
                  fullWidth
                  color={cropMode ? "primary" : "inherit"}
                >
                  {cropMode ? "Exit Crop Mode" : "Enable Crop Mode"}
                </Button>
                {cropMode && cropArea.width > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Crop Area: {Math.round(cropArea.width)} × {Math.round(cropArea.height)}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Reset Button */}
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset} fullWidth>
                Reset All
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" startIcon={<CheckIcon />} disabled={!imageLoaded}>
          Confirm & Use Image
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImageEditorDialog
